import { z } from "zod";
import { CURRENCY_CODES } from "../money/currency.ts";
import { TimeEntrySchema } from "./labour.ts";
import { DiningStyleSchema, ORDER_SOURCES } from "./store.ts";

/**
 * Orders and settlement (`03-data-model.md` § 4, `04-api-surface.md`).
 *
 * Money crosses as minor units in a string, the same as everywhere else in
 * this product. Quantities cross as decimal strings because a weighed item
 * sells 1.250 kg and a JSON number would round it somewhere in the middle.
 */

const MinorUnits = z.string().regex(/^-?\d+$/, "minor units, as digits");
const PositiveMinorUnits = z.string().regex(/^\d+$/, "minor units, as digits");
const Quantity = z.string().regex(/^\d+(\.\d{1,3})?$/, "at most three decimal places");

export const ORDER_ACCEPT_STATUSES = ["pending", "accepted", "rejected"] as const;
export const ORDER_PAY_STATUSES = [
  "unpaid",
  "partly_paid",
  "paid",
  "credit",
  "refunded",
  "cancelled",
] as const;
/**
 * A bill that is still on its table.
 *
 * Deliberately not the same question as "can this bill still be amended",
 * which `openOrder` answers and which also admits a refunded one. A refunded
 * bill has been settled and the party has left.
 */
export const OPEN_BILL_PAY_STATUSES = ["unpaid", "partly_paid"] as const;

export const ORDER_ITEM_STATUSES = ["ordered", "fired", "served", "returned", "voided"] as const;
/** Lines that still count as ordered: the rest have left the bill. */
export const LIVE_ORDER_ITEM_STATUSES = ["ordered", "fired", "served"] as const;
export const ORDER_EVENT_KINDS = [
  "created",
  "accepted",
  "item_added",
  "item_returned",
  "item_voided",
  "item_fired",
  "table_changed",
  "covers_changed",
  "held",
  "resumed",
  "merged",
  "split",
  "discounted",
  "tax_exempted",
  "tax_reinstated",
  "paid",
  "tip_adjusted",
  "refunded",
  "reversed",
  "cancelled",
  "supplies_deducted",
  "member_attached",
  "points_earned",
] as const;

export const OrderItemSchema = z.object({
  id: z.uuid(),
  itemId: z.uuid().nullable(),
  name: z.string(),
  unitPrice: MinorUnits,
  quantity: Quantity,
  categoryId: z.uuid().nullable(),
  categoryName: z.string().nullable(),
  specifications: z.array(z.object({ name: z.string(), price: MinorUnits })),
  toppings: z.array(z.object({ name: z.string(), price: MinorUnits })),
  tags: z.array(z.string()),
  note: z.string().nullable(),
  packingFee: MinorUnits,
  /** When this should be made — 1 starters, 2 mains, 3 dessert. */
  courseNo: z.number().int(),
  /** Which seat ordered it, where a room counts seats. */
  seatNo: z.number().int().nullable(),
  grossAmount: MinorUnits,
  lineDiscount: MinorUnits,
  /**
   * What a promotional campaign took off this line
   * (`MARKETING_PROMOTIONAL_CAMPAIGN`). Apart from `lineDiscount` because the
   * master report breaks the two out, and because one was decided by a
   * cashier and the other by a rule.
   */
  promoDiscount: MinorUnits,
  billShare: MinorUnits,
  netAmount: MinorUnits,
  serviceCharge: MinorUnits,
  isTaxExempt: z.boolean(),
  status: z.enum(ORDER_ITEM_STATUSES),
  firedAt: z.string().nullable(),
  returnedQuantity: Quantity,
  returnReason: z.string().nullable(),
  sort: z.number().int(),
});
export type OrderItem = z.infer<typeof OrderItemSchema>;

export const OrderTaxLineSchema = z.object({
  code: z.string(),
  name: z.string(),
  rateBp: z.number().int(),
  isInclusive: z.boolean(),
  taxableBase: MinorUnits,
  amount: MinorUnits,
  sequence: z.number().int(),
});

export const OrderPaymentSchema = z.object({
  id: z.uuid(),
  paymentMethodId: z.uuid().nullable(),
  methodCode: z.string(),
  methodName: z.string(),
  amount: MinorUnits,
  /** How much of `amount` was gratuity. Tip adjust amends this row. */
  tipAmount: MinorUnits,
  tendered: MinorUnits.nullable(),
  tenderedCurrency: z.enum(CURRENCY_CODES).nullable(),
  change: MinorUnits,
  reference: z.string().nullable(),
  status: z.enum(["captured", "refunded", "reversed", "failed"]),
  refundedAmount: MinorUnits,
  paidAt: z.string(),
});
export type OrderPayment = z.infer<typeof OrderPaymentSchema>;

export const OrderEventSchema = z.object({
  id: z.uuid(),
  kind: z.enum(ORDER_EVENT_KINDS),
  actorName: z.string().nullable(),
  amount: MinorUnits.nullable(),
  payload: z.unknown().nullable(),
  occurredAt: z.string(),
});

export const OrderSchema = z.object({
  id: z.uuid(),
  orderNo: z.string(),
  invoiceNumber: z.string().nullable(),
  pickupCode: z.string().nullable(),
  tableId: z.uuid().nullable(),
  tableName: z.string().nullable(),
  consumerName: z.string().nullable(),
  peopleCount: z.number().int().nullable(),
  acceptStatus: z.enum(ORDER_ACCEPT_STATUSES),
  payStatus: z.enum(ORDER_PAY_STATUSES),
  diningStyle: DiningStyleSchema,
  source: z.enum(ORDER_SOURCES),
  isTaxExempt: z.boolean(),
  isHeld: z.boolean(),
  hasPendingItems: z.boolean(),
  hasBoundSupply: z.boolean(),

  orderAmount: MinorUnits,
  discountAmount: MinorUnits,
  serviceCharge: MinorUnits,
  tableFee: MinorUnits,
  taxTotal: MinorUnits,
  /** Already inside the total. Reported, never added. */
  inclusiveTaxTotal: MinorUnits,
  receivableAmount: MinorUnits,
  /** The gratuity. Outside `receivableAmount` on purpose — see `pos_orders`. */
  tipAmount: MinorUnits,
  paidAmount: MinorUnits,
  refundedAmount: MinorUnits,
  outstandingAmount: MinorUnits,

  currency: z.enum(CURRENCY_CODES),
  minorUnit: z.number().int(),
  employeeName: z.string().nullable(),
  businessDate: z.string(),
  createdAt: z.string(),
  paidAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),

  items: z.array(OrderItemSchema),
  taxLines: z.array(OrderTaxLineSchema),
  payments: z.array(OrderPaymentSchema),
});
export type Order = z.infer<typeof OrderSchema>;

export const OrderDetailSchema = OrderSchema.extend({
  events: z.array(OrderEventSchema),
});
export type OrderDetail = z.infer<typeof OrderDetailSchema>;

const OrderLineInput = z.object({
  itemId: z.uuid(),
  quantity: Quantity.default("1"),
  /**
   * Chosen options, priced per unit. Their prices fold into the line price.
   *
   * A specification is signed, because a specification group's `priceDelta`
   * is: "Small −$0.50" is a size a merchant may configure, and a till that
   * showed it could not send it. A topping is not — it is an addition, and
   * `SaveToppingTemplateSchema` will not let one be worth less than nothing.
   *
   * What the line ends up costing is still checked: see `writeLines`.
   */
  specifications: z.array(z.object({ name: z.string().max(80), price: MinorUnits })).default([]),
  toppings: z.array(z.object({ name: z.string().max(80), price: PositiveMinorUnits })).default([]),
  tags: z.array(z.string().max(40)).default([]),
  note: z.string().max(240).nullable().default(null),
  /** A promo or coupon already resolved against this line. */
  lineDiscount: PositiveMinorUnits.default("0"),
  isTaxExempt: z.boolean().default(false),
  /**
   * For a temporary or open-price item, what the cashier keyed. Ignored when
   * the item has a price of its own, so a till cannot quietly reprice the menu.
   */
  unitPriceOverride: PositiveMinorUnits.nullable().default(null),
  /**
   * Which course this belongs to — 1 starters, 2 mains, 3 dessert.
   *
   * Temporal, where the station is spatial: the station says *where* a line is
   * made, this says *when* it should be. Defaults to 1, so a restaurant that
   * has never thought about coursing fires everything together.
   */
  courseNo: z.number().int().min(1).max(20).default(1),
  /** Which seat ordered it, where a room counts seats. Null is the common case. */
  seatNo: z.number().int().min(1).max(99).nullable().default(null),
});

export const CreateOrderSchema = z.object({
  tableId: z.uuid().nullable().default(null),
  diningStyle: DiningStyleSchema.default("dine_in"),
  source: z.enum(ORDER_SOURCES).default("cashier"),
  peopleCount: z.number().int().positive().nullable().default(null),
  consumerName: z.string().max(120).nullable().default(null),
  isTaxExempt: z.boolean().default(false),
  /**
   * Empty is a **seating**: covers are confirmed at the till and the bill
   * opens before anything has been ordered, which is what makes the table
   * read as taken on every other terminal. `POST /orders/:id/items` still
   * requires a line — sending nothing there is a mistake, opening an empty
   * bill is not.
   */
  lines: z.array(OrderLineInput).default([]),
});

export const AddOrderLinesSchema = z.object({ lines: z.array(OrderLineInput).min(1) });

export const AmendOrderItemSchema = z.object({
  action: z.enum(["return", "void", "fire", "serve"]),
  /** How much of the line came back. Absent returns the whole line. */
  quantity: Quantity.optional(),
  /**
   * Which station is speaking, on a `serve`. Null means the whole line, which
   * is what the back office and the till's service screen send — a pass chip
   * names itself so the other stations keep holding the line.
   */
  stationId: z.uuid().nullable().default(null),
  reason: z.string().max(240).nullable().default(null),
});

export const ChangeTableSchema = z.object({
  tableId: z.uuid().nullable(),
  peopleCount: z.number().int().positive().nullable().default(null),
});

/**
 * Correcting the covers on a bill that is already open.
 *
 * Separate from `ChangeTableSchema` because it is a different act: the party
 * is where it was and one more chair was pulled up. Moving it is a feature a
 * merchant buys; counting it right is not.
 */
export const ChangeCoversSchema = z.object({
  peopleCount: z.number().int().positive().max(99),
});

/** Identifying the diner on an open bill *(verified — member lookup by phone)*. */
export const AttachMemberSchema = z.object({ consumerId: z.uuid() });

export const DiscountOrderSchema = z.object({
  /** A whole-bill discount, prorated across the lines that accept one. */
  amount: PositiveMinorUnits,
  reason: z.string().max(240).nullable().default(null),
  /**
   * The spend-and-save campaign this came off, where one did.
   *
   * A promo is offered rather than applied — `GET /orders/{id}/promos` hands
   * the cashier the campaigns a bill reaches and the cashier chooses — so
   * without this the discount that results is indistinguishable from one typed
   * in by hand, and no campaign can ever be told what it cost. Null is a
   * discount somebody decided on themselves, which is the discount transaction
   * report's business rather than the promo report's.
   */
  promoId: z.uuid().nullable().default(null),
});

export const PayOrderSchema = z.object({
  tenders: z
    .array(
      z.object({
        paymentMethodId: z.uuid(),
        amount: PositiveMinorUnits,
        /**
         * The gratuity left on this tender, on top of `amount`.
         *
         * Per tender rather than per order because a tip belongs to the card
         * it was left on: a split cheque can carry two tips at two rates, and
         * amending one after authorisation amends that row alone. The card is
         * charged `amount + tip`; the bill outstanding is measured against
         * `amount` only, because a tip settles nothing.
         */
        tip: PositiveMinorUnits.default("0"),
        /** What was handed over, where that differs — cash, or a second currency. */
        tendered: PositiveMinorUnits.nullable().default(null),
        tenderedCurrency: z.enum(CURRENCY_CODES).nullable().default(null),
        reference: z.string().max(120).nullable().default(null),
        /**
         * Required to spend a member's stored value once they have set one.
         * A phone number identifies a member; it does not authorise spending
         * their money.
         */
        pin: z.string().max(16).nullable().default(null),
        /**
         * Required to redeem a gift card, and it is the whole credential.
         *
         * A bearer instrument has no PIN by design: the card belongs to whoever
         * holds it. The code standing in for one is why it is long and
         * unguessable rather than sequential.
         */
        giftCardCode: z.string().max(32).nullable().default(null),
      }),
    )
    .min(1),
});

/**
 * Tip adjust after authorisation (table stakes #2).
 *
 * **The new tip, not a delta.** An absolute amount is idempotent on its own: a
 * till draining an offline queue, a double-tap, a retry after a timeout all
 * arrive at the same tip rather than at three of them. A delta would need the
 * idempotency key to be right to be safe; this one is safe when it is not.
 */
export const AdjustTipSchema = z.object({
  amount: PositiveMinorUnits,
  reason: z.string().max(240).nullable().default(null),
});

export const RefundOrderSchema = z.object({
  /** Absent refunds everything that was taken. */
  amount: PositiveMinorUnits.optional(),
  reason: z.string().min(1).max(240),
});

export const CancelOrderSchema = z.object({ reason: z.string().min(1).max(240) });

export const MergeOrdersSchema = z.object({
  /** The orders folded into this one. They keep their history and close. */
  sourceOrderIds: z.array(z.uuid()).min(1),
});

export const SplitOrderSchema = z.object({
  /** Lines moved onto a new order — the classic "separate bills" request. */
  itemIds: z.array(z.uuid()).min(1),
  tableId: z.uuid().nullable().default(null),
});

/**
 * Split a bill by seat: one new order per seat, the original keeping whatever
 * was never attributed to one.
 *
 * A separate route from `split` rather than a mode of it, because it answers a
 * different question. `split` moves lines somebody pointed at; this one asks the
 * bill to divide itself, and the number of bills that come back is not something
 * the caller knew in advance.
 */
export const SplitBySeatSchema = z.object({
  /** Which seats to peel off. Empty means every seat on the bill. */
  seatNos: z.array(z.number().int().min(1).max(99)).max(99).default([]),
});

/** Fire a whole course to the stations that make it. */
export const FireCourseSchema = z.object({
  courseNo: z.number().int().min(1).max(20),
});

/** The chain list adds the store dimension, and nothing else (`12-chain.md` § 4). */
export const ChainOrderSchema = OrderSchema.extend({
  storeId: z.uuid(),
  storeName: z.string(),
});

export type ChainOrder = z.infer<typeof ChainOrderSchema>;

export const OrderListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  size: z.coerce.number().int().positive().max(200).default(20),
  q: z.string().trim().optional(),
  payStatus: z.enum(ORDER_PAY_STATUSES).optional(),
  diningStyle: DiningStyleSchema.optional(),
  source: z.enum(ORDER_SOURCES).optional(),
  tableId: z.uuid().optional(),
  /** Business dates, inclusive. Reports run on the trading day, not the clock. */
  from: z.string().optional(),
  to: z.string().optional(),
});

/**
 * Asking for a file of bills (`ORDER_EXPORT_RECEIPT`).
 *
 * A period is required rather than optional, unlike the list query it
 * otherwise mirrors: a list with no dates is this page of recent orders, and
 * an export with no dates is every bill the store has ever taken.
 */
export const ExportOrdersSchema = z.object({
  format: z.enum(["csv", "json"]).default("csv"),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "a business date"),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "a business date"),
  payStatus: z.enum(ORDER_PAY_STATUSES).optional(),
  q: z.string().trim().max(120).optional(),
});

export const ChainOrderListQuerySchema = OrderListQuerySchema.extend({
  /** One store out of the chain. Absent means all of them. */
  storeId: z.uuid().optional(),
});

/**
 * What a station screen needs, and nothing it does not.
 *
 * The stations sit on the item rather than the ticket: one order's food goes
 * to the pass while its drinks go to the bar, so a ticket spans stations even
 * when a screen is only showing one of them.
 *
 * A line names *every* station it reached, each with its own `servedAt` — a
 * plate assembled at the pass and garnished at the larder is finished when both
 * have stamped, and a screen showing "All" needs to say which are still open.
 */
export const StationTicketSchema = z.object({
  orderId: z.uuid(),
  orderNo: z.string(),
  tableName: z.string().nullable(),
  diningStyle: DiningStyleSchema,
  createdAt: z.string(),
  items: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      stations: z.array(
        z.object({ id: z.uuid(), name: z.string(), servedAt: z.string().nullable() }),
      ),
      quantity: Quantity,
      note: z.string().nullable(),
      specifications: z.array(z.object({ name: z.string(), price: MinorUnits })),
      toppings: z.array(z.object({ name: z.string(), price: MinorUnits })),
      status: z.enum(ORDER_ITEM_STATUSES),
      firedAt: z.string().nullable(),
      /** So a pass works in the order the room eats. */
      courseNo: z.number().int(),
      seatNo: z.number().int().nullable(),
    }),
  ),
});
export type StationTicket = z.infer<typeof StationTicketSchema>;

// ── Shifts (`10-build-plan.md` § Phase 2, "shift handover") ─────────────────

export const ShiftSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  status: z.enum(["open", "closed"]),
  openedByName: z.string().nullable(),
  openedAt: z.string(),
  openingFloat: MinorUnits,
  closedByName: z.string().nullable(),
  closedAt: z.string().nullable(),
  countedCash: MinorUnits.nullable(),
  expectedCash: MinorUnits.nullable(),
  /** `counted − expected`. Negative is short. */
  cashDifference: MinorUnits.nullable(),
  currency: z.enum(CURRENCY_CODES),
  businessDate: z.string(),
  notes: z.string().nullable(),
});
export type Shift = z.infer<typeof ShiftSchema>;

/** What was taken during a shift, by tender and in total. */
export const ShiftTakingsSchema = z.object({
  orderCount: z.number().int(),
  /** Σ receivable across the bills settled in this shift. */
  billed: MinorUnits,
  refunded: MinorUnits,
  net: MinorUnits,
  byMethod: z.array(
    z.object({
      methodCode: z.string(),
      methodName: z.string(),
      taken: MinorUnits,
      refunded: MinorUnits,
      net: MinorUnits,
    }),
  ),
});
export type ShiftTakings = z.infer<typeof ShiftTakingsSchema>;

/** A tender's expected, counted and difference, once a shift is closed. */
export const ShiftTenderReconciliationSchema = z.object({
  methodCode: z.string(),
  methodName: z.string(),
  expected: MinorUnits,
  counted: MinorUnits,
  difference: MinorUnits,
});

export const ShiftDetailSchema = ShiftSchema.extend({
  takings: ShiftTakingsSchema,
  /**
   * Per-rail reconciliation, once the shift is closed and the merchant declared
   * any. Empty on an open shift and on a closed one nobody checked a rail for —
   * an absent answer rather than a zero, because "nobody looked" and "it
   * matched" are different facts about the money.
   */
  reconciliations: z.array(ShiftTenderReconciliationSchema).default([]),
});
export type ShiftDetail = z.infer<typeof ShiftDetailSchema>;

/**
 * The client id the till presents to `POST /device/code` to pair itself
 * (`POS_DEVICE_CLIENT_ID` below). Not a secret — a client id never is, in
 * RFC 8628 — it only tells `validateClient` which device is asking, so an
 * ordinary browser cannot mint a device code for itself and call it a
 * terminal. `STAFF_IDENTIFY_CLIENT_ID` below is the second one
 * `validateClient` admits, for the till asking who is standing at it rather
 * than asking to be paired (`#168`).
 *
 * Both the till and the API read this one constant — `sign-in.tsx` and
 * `lib/pairing.ts` on the till's side, `apps/api/src/auth.ts` on the API's.
 */
export const POS_DEVICE_CLIENT_ID = "tovi-pos";

/**
 * The client id the till presents to `POST /device/code` when it wants to
 * identify a person by scan rather than pair itself (`POS_DEVICE_CLIENT_ID`
 * above is the pairing one). Not a secret — a client id never is, in RFC
 * 8628 — it only tells `validateClient`, and the session hook that follows
 * it, which flow a code belongs to. The till and the staff app both import it
 * from here rather than each hard-coding the string.
 */
export const STAFF_IDENTIFY_CLIENT_ID = "tovi-pos-identify";

/**
 * The till trading a redeemed identification grant for a staff assertion
 * (`POST /store/shifts/grant`).
 */
export const StaffGrantSchema = z.object({
  accessToken: z.string().min(1),
});

/**
 * `POST /store/shifts/override` — a manager approving a till's identify code
 * on somebody else's behalf, for a person whose phone cannot get on: a flat
 * battery, a lost phone, a temp worker with no login at all (`#170`). The
 * till never sees this call; it still polls and redeems the code exactly as
 * it does for a self-approved one, and the grant it exchanges names the
 * employee the manager chose, never the manager.
 */
export const StaffOverrideSchema = z.object({
  /** The till's own identify code, as displayed on its screen. */
  userCode: z.string().min(1),
  employeeId: z.uuid(),
});

export const StaffSessionSchema = z.object({
  /**
   * Carried as `X-Staff-Session`. It says who, and the server authorises as
   * that person; `permissions` below says what their role grants, so the till
   * can be honest about which buttons will work. The server decides either
   * way — this is what to render, never what to enforce.
   */
  token: z.string(),
  expiresAt: z.string(),
  employee: z.object({
    id: z.uuid(),
    name: z.string(),
    roleName: z.string().nullable(),
    /** False for a waiter who takes orders but does not touch the drawer. */
    canOperateRegister: z.boolean(),
    /** Null means no photo; otherwise `GET /store/staff/{id}/photo`'s `v`. */
    photoVersion: z.string().nullable(),
  }),
  /** The same shape `/api/me` hands the back office, for the same reason. */
  permissions: z.array(z.string()),
});

/**
 * `POST /store/shifts/grant/preview` — what a redeemed grant would let a till
 * offer, without spending it (`#219`).
 *
 * The till reads this to choose between "Check in", "Take till" and "Check in
 * and take till" (or the "Check out" equivalents); nothing here is written,
 * so a second preview of the same grant, or a preview followed by a refused
 * clock, costs the grant nothing.
 */
export const GrantPreviewSchema = z.object({
  employee: z.object({
    id: z.uuid(),
    name: z.string(),
    photoVersion: z.string().nullable(),
  }),
  /** The store bought `HR_TIMECLOCK` and this employee's role holds `hr:clock`. */
  timeclock: z.boolean(),
  openEntry: z
    .object({
      id: z.uuid(),
      jobName: z.string(),
      clockedInAt: z.string(),
      /** False when the open entry is elsewhere in the chain. */
      here: z.boolean(),
    })
    .nullable(),
  /** Enabled jobs at this store, in sort order. Empty when `timeclock` is false. */
  jobs: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
export type GrantPreview = z.infer<typeof GrantPreviewSchema>;

/**
 * `POST /store/shifts/grant/clock` — spending the same grant on a punch
 * (`#219`), instead of or alongside taking the till.
 *
 * `jobId` only makes sense on the way in: clocking out closes whichever job
 * the open entry was already on.
 */
export const GrantClockSchema = z
  .object({
    accessToken: z.string().min(1),
    action: z.enum(["in", "out"]),
    jobId: z.uuid().optional(),
    takeTill: z.boolean(),
  })
  .refine((body) => (body.action === "in" ? body.jobId !== undefined : body.jobId === undefined), {
    message: "jobId is required to clock in, and only then.",
    path: ["jobId"],
  });

export const GrantClockResponseSchema = z.object({
  entry: TimeEntrySchema,
  /** The staff assertion when `takeTill` was asked for; null otherwise. */
  session: StaffSessionSchema.nullable(),
});

export const OpenShiftSchema = z.object({
  openingFloat: PositiveMinorUnits.default("0"),
  notes: z.string().max(500).nullable().default(null),
});

/**
 * What a rail says arrived, read off a bank app or a terminal's batch.
 *
 * Cash is not one of these — the drawer carries a float and is counted, and the
 * shift reconciles it directly. Naming it here too would be two answers to one
 * question.
 */
export const ShiftTenderCountSchema = z.object({
  methodCode: z.string().min(1).max(64),
  countedAmount: PositiveMinorUnits,
});
export type ShiftTenderCount = z.infer<typeof ShiftTenderCountSchema>;

export const CloseShiftSchema = z.object({
  /** What was actually counted in the drawer. */
  countedCash: PositiveMinorUnits,
  /**
   * What each non-cash rail says it took. Optional and per-tender: a merchant
   * who only checked two of five apps records two, and the other three simply
   * have no answer rather than a false zero.
   */
  tenderCounts: z.array(ShiftTenderCountSchema).default([]),
  notes: z.string().max(500).nullable().default(null),
});

/** Settling an order tax-free (`ORDER_FREE_VAT`). */
export const SetTaxExemptSchema = z.object({
  isTaxExempt: z.boolean(),
  reason: z.string().max(240).nullable().default(null),
});

// ── Credit (tabs) ───────────────────────────────────────────────────────────

/**
 * A tab: an order settled to somebody's account rather than to money
 * *(verified — `store/credit/orders`)*. Reported separately from takings,
 * because until it is repaid it is a receivable and not a sale that paid.
 */
export const CreditOrderSchema = z.object({
  id: z.uuid(),
  orderId: z.uuid(),
  orderNo: z.string(),
  consumerName: z.string().nullable(),
  amount: MinorUnits,
  settledAmount: MinorUnits,
  outstanding: MinorUnits,
  currency: z.enum(CURRENCY_CODES),
  status: z.enum(["open", "settled", "written_off"]),
  businessDate: z.string(),
  repaymentAt: z.string().nullable(),
  createdAt: z.string(),
});
export type CreditOrder = z.infer<typeof CreditOrderSchema>;

export const RepayCreditSchema = z.object({
  paymentMethodId: z.uuid(),
  /** Absent settles the whole tab. */
  amount: PositiveMinorUnits.optional(),
  reference: z.string().max(120).nullable().default(null),
});

/**
 * The scan-to-pay code for a bill.
 *
 * `payload` is the string a QR encoder turns into pixels — the client draws it,
 * because a bitmap over the wire would be a picture the till cannot resize and
 * the diner app cannot restyle.
 *
 * The amount is echoed back rather than left to the caller to remember. A code
 * is for one bill at one moment: the guest scanning it is about to pay exactly
 * this, and a screen showing a figure that disagrees with the code is the one
 * failure that is worse than no code at all.
 */
export const PaymentQrSchema = z.object({
  payload: z.string(),
  amount: MinorUnits,
  currency: z.enum(CURRENCY_CODES),
  /** What the merchant calls this tender, for the caption above the code. */
  methodName: z.string(),
});
export type PaymentQr = z.infer<typeof PaymentQrSchema>;

export const PaymentQrQuerySchema = z.object({
  paymentMethodId: z.uuid(),
  /**
   * Part-payment. Absent asks for the whole outstanding balance, which is what
   * a guest paying the bill wants and what a cashier splitting it does not.
   */
  amount: PositiveMinorUnits.optional(),
});
